import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Actions from "../actions/Actions";
import styles from "./Slide.module.scss";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import Spinner from "@components/spinner/Spinner";
import Button from "@components/button/Button";
import { getMediaAbsolutePath } from "@services/File";
import { useStandaloneContent } from "@src/hooks/useStandaloneContent";
import RotateIcon from "@src/assets/icons/rotate.svg?react";
import { isHomeEDApp } from "@src/constants";
import { isMobile } from "react-device-detect";
import { resetGoogleTranslateWidget } from "@src/utils/helpers";

type Props = {
  data: string;
  isFullScreen: boolean;
  isPreview?: boolean;
  goBack?: () => void;
  pageNumber: number;
  changePageNumber: (value: number) => void;
  startLesson?: () => void;
};

function Slide(props: Props) {
  const {
    data,
    isFullScreen,
    isPreview,
    goBack,
    pageNumber,
    changePageNumber,
    startLesson,
  } = props;

  // Initialize PDF worker singleton

  const {
    content,
    totalPages,
    setTotalPages,
    accountLocked,
    isNewLessonLayout,
    isPlatformTower,
    requestSilentMarkComplete,
  } = useStandaloneContent();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const autoCompleteTimerRef = useRef<number | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);

  const handleBack = useCallback(() => {
    resetGoogleTranslateWidget();
    goBack?.();
  }, [goBack]);

  useEffect(() => {
    setTotalPages(1);
  }, [data, setTotalPages]);

  useEffect(() => {
    return () => {
      if (autoCompleteTimerRef.current != null) {
        window.clearTimeout(autoCompleteTimerRef.current);
        autoCompleteTimerRef.current = null;
      }
    };
  }, []);

  const fileObj = useMemo(() => {
    return getMediaAbsolutePath(data);
  }, [data]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }) => {
      setTotalPages(numPages);
      if (
        content?.lastInLesson &&
        (!isHomeEDApp || !accountLocked) &&
        !isPreview
      ) {
        if (autoCompleteTimerRef.current != null) {
          window.clearTimeout(autoCompleteTimerRef.current);
        }
        autoCompleteTimerRef.current = window.setTimeout(() => {
          autoCompleteTimerRef.current = null;
          requestSilentMarkComplete();
        }, 3500);
      }
    },
    [
      setTotalPages,
      content?.lastInLesson,
      accountLocked,
      isPreview,
      requestSilentMarkComplete,
    ],
  );

  const calculateSize = useCallback(() => {
    if (wrapperRef.current) {
      const { width, height } = wrapperRef.current.getBoundingClientRect();
      setWidth(width);
      setHeight(height);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("orientationchange", calculateSize);

    return () => {
      window.removeEventListener("orientationchange", calculateSize);
    };
  }, [calculateSize]);

  useLayoutEffect(() => {
    window.addEventListener("resize", calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener("resize", calculateSize);
    };
  }, [calculateSize]);

  useEffect(() => {
    if (width) {
      setTimeout(calculateSize, 50);
    }
  }, [calculateSize, width]);

  const prevDisabled = useMemo(() => pageNumber <= 1, [pageNumber]);
  const isLast = useMemo(
    () => pageNumber >= totalPages,
    [pageNumber, totalPages],
  );

  const handleLeft = useCallback(() => {
    changePageNumber(Math.max(pageNumber - 1, 1));
  }, [pageNumber, changePageNumber]);

  const handleRight = useCallback(() => {
    changePageNumber(Math.min(pageNumber + 1, totalPages));
  }, [totalPages, pageNumber, changePageNumber]);

  const prevButton = useMemo(() => {
    if (prevDisabled) return null;
    return (
      <Button
        size={"extraSmall"}
        onClick={handleLeft}
        className={styles.previousBtn}
        label={"PREVIOUS"}
      />
    );
  }, [prevDisabled, handleLeft]);

  const nextButton = useMemo(() => {
    if (isLast) return null;
    return <Button size={"extraSmall"} onClick={handleRight} label={"NEXT"} />;
  }, [isLast, handleRight]);

  const ratio = useMemo(() => {
    if (width && height) {
      return width / height;
    }
    return 1;
  }, [width, height]);

  if (!fileObj) return null;

  return (
    <Actions
      fullScreen={isFullScreen}
      leftButton={prevButton}
      rightButton={nextButton}
      totalPages={totalPages}
      pageNumber={pageNumber}
      isPreview={isPreview}
      goBack={goBack}
    >
      <div
        className={`${styles.wrapper} ${!isPreview ? styles.blackfullScreen : ""}`}
        ref={wrapperRef}
      >
        <div
          className={isFullScreen ? styles.fullContent : styles.content}
          id="pdfWrapper"
        >
          <Document
            file={fileObj}
            onLoadSuccess={onDocumentLoadSuccess}
            className={isPreview ? styles.previewDocument : styles.document}
            loading={
              <div
                className={styles.loading}
                style={{ height: isMobile ? 300 : 500 }}
              >
                <Spinner />
              </div>
            }
          >
            <Page
              loading={
                <div className={styles.loading}>
                  <Spinner />
                </div>
              }
              pageNumber={pageNumber}
              width={ratio > 1.7 ? 0 : width}
              height={height}
              className={isPreview ? styles.roundCanvas : ""}
            />
          </Document>
          {isPreview &&
          !isHomeEDApp &&
          !isNewLessonLayout &&
          !isPlatformTower ? (
            <div className={styles.previewBeginLesson}>
              <div className={styles.pulse}>
                <Button
                  label={"Begin Lesson"}
                  color="white"
                  className={styles.beginLesson}
                  onClick={() => startLesson && startLesson()}
                />
              </div>
            </div>
          ) : null}
        </div>
        <div className={styles.rotate}>
          <RotateIcon width="60" height="60" className={styles.rorateIcon} />
          <div className={styles.rotateTitle}>Rotate your phone,</div>
          <div className={styles.rotateDetail}>
            for a better reading experience.
          </div>
        </div>
      </div>
    </Actions>
  );
}

export default Slide;
